/**
 * Asset register — individually-tagged physical kit handed out at each
 * station. One row per real-world asset, ever (the global pool model).
 *
 * Status transitions:
 *   available  ⇄  assigned  →  issued  (KitIssued event, future hook)
 *                    ↓
 *                 retired
 *
 * The `assigned_to_person_id` column captures pre-day pre-assignment.
 * The KitIssued event remains the source of truth for "this asset was
 * physically handed over"; status='issued' on the row is a denormalised
 * mirror to make the UI fast.
 */

import type { Station } from './kitTemplate';

export const ASSET_STATUSES = [
  'available',
  'assigned',
  'issued',
  'retired',
] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

export interface Asset {
  id: string;
  asset_tag: string;
  description: string;
  station: Station;
  category: string | null;
  status: AssetStatus;
  assigned_to_person_id: string | null;
  /**
   * Display name of the assignee. Joined server-side from the people
   * table when the asset is listed, so the AssetsList row can show
   * "Alice Wong" instead of "View person" without an extra round-trip.
   * Null when assigned_to_person_id is null or the assignee row has
   * since been pseudonymised (GDPR).
   */
  assigned_to_full_name: string | null;
  /**
   * The asset's unique identifier in the CAFM (Planon). Null until the
   * CAFM team assigns one; unique across all assets once set.
   */
  syscode: string | null;
  /** Free-form: { serial, imei, mac_address, registration, ... }. */
  metadata: Record<string, unknown>;
  notes: string | null;
  /**
   * TUPE / existing-kit marker. When true the asset is already held by the
   * person it is assigned to, so the station desk CONFIRMS possession
   * instead of issuing it. Drives the confirm panel in the kit drawer.
   */
  confirm_on_arrival: boolean;
  created_at: string;
  updated_at: string;
}

/** The registration plate of a vehicle asset (metadata.registration), or null. */
export function assetRegistration(
  asset: Pick<Asset, 'metadata'>,
): string | null {
  const reg = asset.metadata?.registration;
  return typeof reg === 'string' && reg.trim() ? reg.trim() : null;
}

/**
 * The most meaningful identifier to show for an asset: for a Fleet vehicle
 * with a registration plate, the plate; otherwise the asset tag. Keeps the
 * Equipment list and the kit drawer showing "AB23 XYZ" for vans rather than
 * an opaque tag.
 */
export function assetPrimaryLabel(
  asset: Pick<Asset, 'station' | 'asset_tag' | 'metadata'>,
): string {
  if (asset.station === 'Fleet') {
    const reg = assetRegistration(asset);
    if (reg) return reg;
  }
  return asset.asset_tag;
}

/** Create / update payload — id, status transitions and timestamps server-managed. */
export interface AssetInput {
  asset_tag: string;
  description: string;
  station: Station;
  category?: string | null;
  syscode?: string | null;
  metadata?: Record<string, unknown>;
  notes?: string | null;
  confirm_on_arrival?: boolean;
}
